import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';

const SECTIONS = [
  { id: 'about', navKey: 'nav.about' },
  { id: 'experience', navKey: 'nav.experience' },
  { id: 'projects', navKey: 'nav.projects' },
  { id: 'skills', navKey: 'nav.skills' },
  { id: 'publications', navKey: 'nav.publications' },
];

const PAGES = [
  { path: '/books', navKey: 'nav.books' },
  { path: '/contact', navKey: 'nav.contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { t } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isHome) { setActiveSection(''); return; }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome]);

  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    setIsOpen(false);
    if (isHome) {
      e.preventDefault();
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isPageActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
      scrolled || isOpen
        ? 'bg-white/97 dark:bg-[#0A0A0A]/97 backdrop-blur-xl shadow-sm border-b border-gray-200 dark:border-white/[0.08]'
        : 'bg-white/80 dark:bg-[#0A0A0A]/80 backdrop-blur-md border-b border-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link
            to="/"
            onClick={() => { setIsOpen(false); if (isHome) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="group flex items-center gap-1.5 shrink-0"
          >
            <span className="text-sm font-black tracking-tight text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary transition-colors">
              Cristian
            </span>
            <span className="text-sm font-black tracking-tight text-primary transition-colors">
              Tesconi
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden xl:flex items-center gap-0.5">
            {SECTIONS.map(({ id, navKey }) => {
              const isActive = activeSection === id && isHome;
              return (
                <a
                  key={id}
                  href={`/#${id}`}
                  onClick={(e) => handleSectionClick(e, id)}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'text-primary dark:text-primary'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/60 dark:hover:bg-white/5'
                  }`}
                >
                  {t(navKey)}
                  {isActive && (
                    <span className="absolute bottom-1 left-3 right-3 h-0.5 bg-primary rounded-full" />
                  )}
                </a>
              );
            })}

            <span className="w-px h-4 bg-gray-200 dark:bg-white/10 mx-1.5" />

            {PAGES.map(({ path, navKey }) => (
              <Link
                key={path}
                to={path}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isPageActive(path)
                    ? 'text-primary dark:text-primary bg-primary/10'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/60 dark:hover:bg-white/5'
                }`}
              >
                {t(navKey)}
              </Link>
            ))}

            <span className="w-px h-4 bg-gray-200 dark:bg-white/10 mx-1.5" />

            <div className="flex items-center gap-1.5">
              <LanguageToggle />
              <ThemeToggle />
            </div>
          </div>

          {/* Mobile controls */}
          <div className="xl:hidden flex items-center gap-1.5">
            <LanguageToggle />
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(v => !v)}
              aria-label="Toggle navigation menu"
              className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      {isOpen && (
        <div className="xl:hidden border-t border-gray-100 dark:border-white/[0.08]">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-0.5">
            <p className="px-3 pt-1 pb-2 text-[10px] font-bold tracking-widest uppercase text-gray-400 dark:text-gray-600">
              {t('nav.sections', 'Sezioni')}
            </p>
            {SECTIONS.map(({ id, navKey }) => {
              const isActive = activeSection === id && isHome;
              return (
                <a
                  key={id}
                  href={`/#${id}`}
                  onClick={(e) => handleSectionClick(e, id)}
                  className={`flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-primary bg-primary/10'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-primary dark:hover:text-primary'
                  }`}
                >
                  {t(navKey)}
                </a>
              );
            })}

            <p className="px-3 pt-3 pb-2 text-[10px] font-bold tracking-widest uppercase text-gray-400 dark:text-gray-600">
              {t('nav.pages', 'Pagine')}
            </p>
            {PAGES.map(({ path, navKey }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isPageActive(path)
                    ? 'text-primary bg-primary/10'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-primary dark:hover:text-primary'
                }`}
              >
                {t(navKey)}
              </Link>
            ))}

            <div className="pt-2" />
          </div>
        </div>
      )}
    </nav>
  );
}
